/**
 * The company roles and what each may do, as data (PRD 0003, section 5). A control asks
 * `can` rather than comparing role names. The backend stays the gate: a `403 forbidden`
 * from any call is shown as a refusal, never assumed impossible.
 */
export type Role = 'owner' | 'admin' | 'member' | 'viewer'

export const ROLES: readonly Role[] = ['owner', 'admin', 'member', 'viewer']

export function isRole(value: unknown): value is Role {
  return typeof value === 'string' && (ROLES as readonly string[]).includes(value)
}

/** The rows of the matrix that have a control. `editDictionary` also covers assigning a value (GRO-157). */
export type Action = 'seePendingInvites' | 'invite' | 'changeRole' | 'removeMember' | 'connectAccount' | 'readActivity' | 'share' | 'editDictionary' | 'editSettings'

const MANAGERS: readonly Role[] = ['owner', 'admin']
const EDITORS: readonly Role[] = ['owner', 'admin', 'member']

export function can(role: Role, action: Action): boolean {
  switch (action) {
    case 'seePendingInvites':
    case 'invite':
    case 'changeRole':
    case 'removeMember':
    case 'connectAccount':
    case 'readActivity':
    case 'share':
    case 'editSettings':
      return MANAGERS.includes(role)
    case 'editDictionary':
      return EDITORS.includes(role)
  }
}

/** Any role deletes the demo company; only an Owner deletes a real one (GRO-131, D4). */
export function canDeleteCompany(role: Role, isDemo: boolean): boolean {
  return isDemo || role === 'owner'
}

/** Owner: all four. Admin: up to Admin. Member and Viewer: none. */
export function invitableRoles(role: Role): Role[] {
  if (role === 'owner') return [...ROLES]
  if (role === 'admin') return ['admin', 'member', 'viewer']
  return []
}

/**
 * Whether `actor` may change `target`'s role, and to `to` when given. An Owner changes
 * anyone; an Admin changes non-Owners and never to Owner; others change nobody.
 */
export function canChangeMember(actor: Role, target: Role, to?: Role): boolean {
  if (actor === 'owner') return true
  if (actor === 'admin') return target !== 'owner' && to !== 'owner'
  return false
}

/** Whether `actor` may remove `target`. Leaving is not removal. */
export function canRemoveMember(actor: Role, target: Role): boolean {
  if (actor === 'owner') return true
  if (actor === 'admin') return target !== 'owner'
  return false
}
